class PowerBiService {
    constructor() {
        this.models = null;
        this.initialized = false;
        this.debug = false;
        this.activeReports = new Map();
        this.tokenRefreshCallbacks = new Map(); // Store refresh callbacks
    }

    log(...args) {
        if (this.debug) console.log('[PowerBi]', ...args);
    }

    error(...args) {
        console.error('[PowerBi]', ...args);
    }

    async waitForPowerBI() {
        if (this.initialized) return this.models;

        return new Promise((resolve, reject) => {
            const check = () => {
                if (window.powerbi && window['powerbi-client']) {
                    this.models = window['powerbi-client'].models;
                    this.initialized = true;
                    this.log('PowerBI loaded');
                    resolve(this.models);
                } else {
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }

    getLayoutType() {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const isPortrait = window.matchMedia('(orientation: portrait)').matches;

        const layoutType = isMobile
            ? (isPortrait
                ? this.models.LayoutType.MobilePortrait
                : this.models.LayoutType.MobileLandscape)
            : this.models.LayoutType.Custom;

        this.log('Layout type determined:', layoutType);
        return layoutType;
    }

    getHiddenCommands() {
        this.log('getHiddenCommands called');

        if (!this.models) {
            this.error('Models not initialized in getHiddenCommands');
            throw new Error('PowerBI models not initialized');
        }

        const commands = [
            {
                copy: { displayOption: this.models.CommandDisplayOption.Hidden },
                drill: { displayOption: this.models.CommandDisplayOption.Hidden },
                drillthrough: { displayOption: this.models.CommandDisplayOption.Hidden },
                expandCollapse: { displayOption: this.models.CommandDisplayOption.Hidden },
                includeExclude: { displayOption: this.models.CommandDisplayOption.Hidden },
                groupVisualContainers: { displayOption: this.models.CommandDisplayOption.Hidden },
                summarize: { displayOption: this.models.CommandDisplayOption.Hidden },
                clearSelection: { displayOption: this.models.CommandDisplayOption.Hidden },
                exportData: { displayOption: this.models.CommandDisplayOption.Hidden },
                removeVisual: { displayOption: this.models.CommandDisplayOption.Hidden },
                sort: { displayOption: this.models.CommandDisplayOption.Hidden },
                spotlight: { displayOption: this.models.CommandDisplayOption.Hidden },
                insightsAnalysis: { displayOption: this.models.CommandDisplayOption.Hidden },
                addComment: { displayOption: this.models.CommandDisplayOption.Hidden },
                search: { displayOption: this.models.CommandDisplayOption.Hidden },
                seeData: { displayOption: this.models.CommandDisplayOption.Hidden }
            }
        ];

        this.log('Hidden commands configured');
        return commands;
    }

    getBaseConfig() {
        this.log('getBaseConfig called');

        if (!this.models) {
            this.error('Models not initialized in getBaseConfig');
            throw new Error('PowerBI models not initialized');
        }

        const config = {
            type: 'report',
            tokenType: this.models.TokenType.Embed,
            permissions: this.models.Permissions.Read,
            viewMode: this.models.ViewMode.View,
            settings: {
                layoutType: this.getLayoutType(),
                customLayout: {
                    displayOption: this.models.DisplayOption.FitToPage,
                },
                panes: {
                    filters: { visible: false },
                    pageNavigation: { visible: false }
                },
                commands: this.getHiddenCommands()
            }
        };

        this.log('Base config created');
        return config;
    }

    createReportConfig(reportId, embedUrl, accessToken) {
        this.log('createReportConfig called with:', { reportId, embedUrl, accessToken: accessToken ? '***' : 'MISSING' });

        const baseConfig = this.getBaseConfig();
        const config = {
            ...baseConfig,
            id: reportId,
            embedUrl: embedUrl,
            accessToken: accessToken
        };

        this.log('Report config created');
        return config;
    }

    async fetchReportData(reportKey) {
        this.log('Fetching report data for:', reportKey);
        const response = await fetch(`/powerbi/report/${reportKey}`, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                'Accept': 'application/json'
            }
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();
        if (!result.success) throw new Error(result.error);

        this.log('Report data fetched successfully');
        return result.data;
    }

    // Token refresh handler
    async handleTokenExpiration(report, containerId, reportKey) {
        this.log('Setting up token expiration handler for:', containerId);

        const refreshCallback = async (error) => {
            this.log('Token expiration detected:', error);

            if (error && error.detail &&
                (error.detail.errorCode === 'TokenExpired' ||
                    error.detail.message?.includes('token') ||
                    error.detail.message?.includes('expired'))) {

                this.log('Attempting to refresh token for report:', reportKey);

                try {
                    // Fetch new token
                    const newReportData = await this.fetchReportData(reportKey);
                    this.log('New token fetched successfully');

                    // Refresh the report with new token
                    await report.refresh(newReportData.embedToken);
                    this.log('Token refreshed successfully');

                    // Show success message to user
                    this.showTempMessage(containerId, 'Report refreshed successfully', 'success');

                } catch (refreshError) {
                    this.error('Failed to refresh token:', refreshError);
                    this.showTempMessage(containerId, 'Session expired. Please reload the report.', 'error');

                    // If refresh fails, we might need to re-embed the report
                    setTimeout(() => {
                        this.reloadReport(containerId, reportKey);
                    }, 3000);
                }
            }
        };

        // Store the callback for cleanup
        this.tokenRefreshCallbacks.set(containerId, refreshCallback);

        // Listen for token expiration errors
        report.on('error', refreshCallback);
    }

    // Show temporary message to user
    showTempMessage(containerId, message, type = 'info') {
        const container = document.getElementById(containerId);
        if (!container) return;

        const alertClass = type === 'error' ? 'alert-danger' :
            type === 'success' ? 'alert-success' : 'alert-info';

        const messageElement = document.createElement('div');
        messageElement.className = `alert ${alertClass} alert-dismissible fade show`;
        messageElement.style.position = 'absolute';
        messageElement.style.top = '10px';
        messageElement.style.left = '50%';
        messageElement.style.transform = 'translateX(-50%)';
        messageElement.style.zIndex = '1000';
        messageElement.style.minWidth = '300px';
        messageElement.innerHTML = `
            ${message}
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;

        container.appendChild(messageElement);

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            if (messageElement.parentNode) {
                messageElement.remove();
            }
        }, 5000);
    }

    // Reload entire report if token refresh fails
    async reloadReport(containerId, reportKey) {
        this.log('Reloading report:', reportKey);

        try {
            await this.loadReportIntoModal({
                reportKey: reportKey,
                modalContainerId: containerId
            });
        } catch (error) {
            this.error('Failed to reload report:', error);
            this.showError(containerId, 'Failed to reload report. Please close and reopen.');
        }
    }

    // Proper cleanup method that PowerBI respects
    async destroyReport(containerId) {
        const container = document.getElementById(containerId);
        if (!container) return;

        // Get the report instance
        const report = this.activeReports.get(containerId);

        if (report) {
            this.log(`Destroying report in container: ${containerId}`);

            try {
                // Remove token refresh callback
                const refreshCallback = this.tokenRefreshCallbacks.get(containerId);
                if (refreshCallback && typeof report.off === 'function') {
                    report.off('error', refreshCallback);
                    this.tokenRefreshCallbacks.delete(containerId);
                }

                // Remove all event listeners
                if (typeof report.off === 'function') {
                    report.off('loaded');
                    report.off('rendered');
                    report.off('error');
                }

                // Reset the report
                if (typeof powerbi.reset === 'function') {
                    powerbi.reset(container);
                }

                // Remove from active reports
                this.activeReports.delete(containerId);

            } catch (error) {
                this.error('Error during report destruction:', error);
            }
        }

        // Always clear the container HTML
        container.innerHTML = '<div class="powerbi-loading" style="display: none;">Loading Insight...</div>';
    }

    async embedReport(containerId, reportId, embedUrl, accessToken, reportKey = null) {
        this.log('embedReport called:', { containerId, reportId, embedUrl, accessToken: accessToken ? '***' : 'MISSING', reportKey });

        await this.waitForPowerBI();

        const container = document.getElementById(containerId);
        if (!container) {
            this.error(`Container #${containerId} not found`);
            throw new Error(`Container #${containerId} not found`);
        }

        this.log('Creating report config');
        const config = this.createReportConfig(reportId, embedUrl, accessToken);

        this.log('Embedding report with config');

        try {
            const report = powerbi.embed(container, config);
            this.log('powerbi.embed() completed');

            // Store the report reference
            this.activeReports.set(containerId, report);
            this.log('Active reports:', Array.from(this.activeReports.keys()));

            // Set up token expiration handling if reportKey is provided
            if (reportKey) {
                await this.handleTokenExpiration(report, containerId, reportKey);
            }

            return report;
        } catch (error) {
            this.error('Error in powerbi.embed():', error);
            throw error;
        }
    }

    async resizeReportContainer(report, containerId) {
        this.log('resizeReportContainer called:', { containerId, report: !!report });

        if (!report) return;

        try {
            const pages = await report.getPages();
            this.log('Pages retrieved:', pages);

            if (!pages || pages.length === 0) {
                this.log('No pages available');
                return;
            }

            const activePage = pages.find(page => page.isActive);
            this.log('Active page found:', activePage);

            if (!activePage) {
                this.log('No active page found');
                return;
            }

            const isMobile = window.matchMedia('(max-width: 767px)').matches;
            const pageSize = isMobile && activePage.mobileSize
                ? activePage.mobileSize
                : activePage.defaultSize;

            this.log('Page size:', pageSize);

            if (pageSize) {
                const container = document.getElementById(containerId);
                let { width, height } = pageSize;

                if (isMobile) {
                    width = Math.min(width, window.innerWidth * 0.9);
                    height = Math.min(height, window.innerHeight * 0.7);
                }

                this.log(`Setting container size: ${width}x${height}`);
                container.style.width = width + 'px';
                container.style.height = height + 'px';
            }
        } catch (error) {
            this.error('Error resizing report container:', error);
        }
    }

    // Main method for loading reports into modals
    async loadReportIntoModal(config) {
        const { reportKey, modalContainerId } = config;
        this.log('Loading report into modal:', reportKey);

        try {
            const container = document.getElementById(modalContainerId);
            if (!container) throw new Error('Container not found');

            // Properly destroy any existing report
            await this.destroyReport(modalContainerId);

            // Show loading state
            container.innerHTML = '<div class="powerbi-loading">Loading Insight...</div>';

            // Get fresh report data
            const reportData = await this.fetchReportData(reportKey);

            // Wait for PowerBI
            await this.waitForPowerBI();

            this.log('Embedding report');

            // Embed the report with token refresh support
            const report = await this.embedReport(modalContainerId, reportData.reportId, reportData.embedUrl, reportData.embedToken, reportKey);

            // Set up event listeners
            return new Promise((resolve, reject) => {
                let hasResolved = false;
                let renderTimeout;

                const cleanup = () => {
                    if (renderTimeout) clearTimeout(renderTimeout);
                    if (!hasResolved) hasResolved = true;
                };

                const renderedHandler = () => {
                    this.log('🔥 Report rendered successfully');
                    cleanup();
                    this.resizeReportContainer(report, modalContainerId);
                    resolve(report);
                };

                const errorHandler = (event) => {
                    // Don't handle token errors here - they're handled by handleTokenExpiration
                    if (event.detail &&
                        (event.detail.errorCode === 'TokenExpired' ||
                            event.detail.message?.includes('token') ||
                            event.detail.message?.includes('expired'))) {
                        this.log('Token error handled by refresh mechanism');
                        return;
                    }

                    this.error('🔥 PowerBI error event:', event.detail);
                    cleanup();
                    container.innerHTML = `<span class="powerbi-loading">Failed to load the report. Please try again.</span>`;
                    reject(new Error(`PowerBI error: ${event.detail?.message || 'Unknown'}`));
                };

                // Add event listeners
                report.on('loaded', () => this.log('🔥 Report loaded successfully'));
                report.on('rendered', renderedHandler);
                report.on('error', errorHandler);

                // Handle window resize
                const resizeHandler = () => this.resizeReportContainer(report, modalContainerId);
                window.addEventListener('resize', resizeHandler);
                report.resizeHandler = resizeHandler;

                // Timeout fallback
                renderTimeout = setTimeout(() => {
                    if (!hasResolved) {
                        this.error('PowerBI embedding timeout');
                        cleanup();
                        container.innerHTML = '<div class="alert alert-warning">Report is taking longer than expected to load.</div>';
                        resolve(report);
                    }
                }, 60000);

            });

        } catch (error) {
            this.error('Failed to load report:', error);
            const container = document.getElementById(modalContainerId);
            if (container) {
                container.innerHTML = `<span class="powerbi-loading">Failed to load the report. Please try again.</span>`;
            }
            throw error;
        }
    }

    // Method for views with both main view and modal
    async initializeMainView(config) {
        const { reportId, embedUrl, accessToken, mainContainerId, reportKey } = config;

        // Initialize main report immediately
        this.embedReport(mainContainerId, reportId, embedUrl, accessToken, reportKey)
            .then(report => {
                if (report) {
                    report.on('rendered', () => {
                        this.log('Main report rendered');
                        this.resizeReportContainer(report, mainContainerId);
                    });

                    window.addEventListener('resize', () => {
                        this.resizeReportContainer(report, mainContainerId);
                    });
                }
            })
            .catch(error => {
                this.error('Failed to embed main report:', error);
            });
    }

    setupSingleModal(config) {
        const { modalContainerId, modalId } = config;
        const modal = document.getElementById(modalId);

        if (modal) {
            modal.addEventListener('hidden.bs.modal', () => {
                this.log('Modal hidden - cleaning up report');
                this.destroyReport(modalContainerId);
            });

            modal.addEventListener('show.bs.modal', () => {
                this.log('Modal shown - preparing container');
                const container = document.getElementById(modalContainerId);
                if (container) {
                    // Ensure loading state is ready
                    if (!container.querySelector('.powerbi-loading')) {
                        container.innerHTML = '<div class="powerbi-loading" style="display: none;">Loading Insight...</div>';
                    }
                }
            });
        }
    }

    showError(containerId, message) {
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = `<p>${message}</p>`;
        }
    }
}

window.PowerBiService = new PowerBiService();
